// src/components/ui/QuotasMonitorer.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';

import { LoaderCircle, Target, CheckCircle, AlertCircle } from 'lucide-react';
import Button from '../ui/Button';

import API_CONFIG from '../../config/api';

import QuotasMonitorerListDisplay from './QuotasMonitorerListDisplay';

const BASE_URL = API_CONFIG.BASE_URL;

function QuotasMonitorer({ surveyId }) {
	
	
	const navigate = useNavigate();
	const [quotasObject, setQuotasObject] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const { authenticatedFetch } = useAuthenticatedApi();

	const fetchQuotas = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const url = BASE_URL + `/active_surveys/quotas/list-quotas/${surveyId}`
			const result = await authenticatedFetch(url, {}, 'GET');
			
			if (result.status === 404) {
				navigate('/404', { state: { message: `survey with survey_id: ${surveyId} is not found`} });
				return;
			}

			if (result.status === 401) {
				setError("You are not authorized to access this survey, please check that you have the proper access rights and that it is an active survey");
				setQuotasObject(null); // Clear survey data on unauthorized
				return;
			}

			if (!result.ok) {
				let errorMessage = `Failed to fetch quotas: Server responded with status ${result.status}.`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {
				}
				console.error("QuotasMonior API Error:", errorMessage, result);
				setError(errorMessage);
				return; // Stop further execution
			}

			const datas = await result.json();
			if (datas) {
				setQuotasObject(datas.quotas || []);
			} else {
				console.error("API returned empty or invalid data structure:", datas);
				setError("Error API returned empty or invalid data structure. Please try later");
			}

		} catch (err) {
			console.error("Network or unexpected error while fetching quotas:", err);
			setError(`Failed to load quotas for survey ${surveyId}. Please check your connection and try again`);
			setQuotasObject([]);
		} finally {
			setIsLoading(false);
		}

	}, [surveyId]);


	useEffect(() => {
		fetchQuotas();
	}, [fetchQuotas])

	if (isLoading) {
		return (
			<div className="flex justify-center items-center py-12">
				<LoaderCircle className="h-8 w-8 animate-spin text-blue-500" />
				<span className="ml-2 text-gray-600">Loading quotas...</span>
			</div>
		);
	}

	if (error) {
		return (
			<div className="text-center py-12">
				<p className="text-red-500 mb-4">{error}</p>
				<Button onClick={fetchQuotas} variant="outline">
					Retry
				</Button>
			</div>
		);
	}

	if (!quotasObject || quotasObject.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
				<Target className="h-12 w-12 text-gray-400 mb-2" />
				<p className="text-gray-500 text-lg">No quotas found for this survey</p>
			</div>
		);
	}

	return (
		<div className="h-[800px] overflow-y-auto pr-2">
			<QuotasMonitorerListDisplay quotasObject={quotasObject} />
		</div>
		);
}

export default QuotasMonitorer;